import { assert } from '@ember/debug';
import { concat } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { QueryParamsSource } from '@warp-drive/core/types/params';
import { type IntlService, t } from 'ember-intl';
import PowerSelect from 'ember-power-select/components/power-select';
import { trackedFunction } from 'reactiveweb/function';

import { query } from 'umg-ember-backend/data/builders/umg-rest';
import type Store from 'umg-ember-backend/services/store';

type FilterValue = string | number | boolean | null | undefined;
type Filters = Record<string, FilterValue>;
type OptionValue = string | number | boolean;
type Option = { label: string; value: OptionValue };
type QueryParams = Exclude<QueryParamsSource, URLSearchParams>;

interface RelationshipModel {
  id: string;
  [property: string]: unknown;
}

interface RelationshipFilter {
  key: string;
  recordType: string;
  labelKey?: string;
  valueKey?: string;
  useLocaleFilter?: boolean;
  queryParams?: QueryParams;
  options?: { key: string; value: OptionValue }[];
}

interface Signature {
  Args: {
    filter: RelationshipFilter;
    activeFilters: Filters;
    onChangeFilter: (filters: Filters) => void;
  };
}

export default class SelectRelationship extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: InstanceType<typeof Store>;

  get options(): Option[] {
    const { labelKey = 'displayLabel', valueKey = 'id' } = this.args.filter;

    const customOptions = (this.args.filter.options ?? []).map(
      ({ key, value }) => ({
        label: key,
        value,
      }),
    );
    const relationshipOptions = (this.loadOptions.value ?? []).map((record) => {
      const label = record[labelKey];
      const value = record[valueKey];

      assert(
        `Relationship filter ${this.args.filter.key} requires a string or number for ${labelKey}.`,
        typeof label === 'string' || typeof label === 'number',
      );
      assert(
        `Relationship filter ${this.args.filter.key} requires a string or number for ${valueKey}.`,
        typeof value === 'string' || typeof value === 'number',
      );

      return { label: String(label), value };
    });

    return [...customOptions, ...relationshipOptions];
  }

  get selected() {
    const value = this.args.activeFilters[this.args.filter.key];

    if (value === null || value === undefined) {
      return undefined;
    }

    return this.options.find(
      (option) => String(option.value) === String(value),
    );
  }

  loadOptions = trackedFunction(this, async () => {
    const {
      recordType,
      queryParams,
      useLocaleFilter = false,
    } = this.args.filter;
    const locale = useLocaleFilter ? this.intl.primaryLocale : undefined;
    const { content } = await this.store.request<{
      data: RelationshipModel[];
    }>(
      query(recordType, {
        ...queryParams,
        ...(locale ? { locale } : {}),
        'limit': 1000,
        'filter[cmsState]': 2,
      }),
    );

    return content.data;
  });

  setFilter = (selected: Option | null) => {
    const filters = { ...this.args.activeFilters };

    if (selected) {
      filters[this.args.filter.key] = selected.value;
    } else {
      delete filters[this.args.filter.key];
    }

    this.args.onChangeFilter(filters);
  };

  <template>
    <PowerSelect
      @allowClear={{true}}
      @options={{this.options}}
      @selected={{this.selected}}
      @searchEnabled={{true}}
      @searchField='label'
      @placeholder={{t
        'tables.placeholder-filter'
        filter=(t (concat 'tables.filters.' @filter.key))
      }}
      @onChange={{this.setFilter}}
      data-test-relationship-filter={{@filter.key}}
      as |option|
    >
      {{option.label}}
    </PowerSelect>
  </template>
}
